import Footer from "../../../../Components/Footer/Footer";
import { Button, Card, Form, Row, Col } from "react-bootstrap";
import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from "axios";
import { toast } from 'react-hot-toast';
import Loading from 'react-fullscreen-loading';
import countryList from 'react-select-country-list';
import Select from 'react-select';
import TurnstileCaptcha from '../../../../Components/Captcha/TurnstileCaptcha';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './phone-input.css';
import '../Signin/Signin.css';
import './Signup.css';
import logo from '../../../../assets/Logo/cadomationnewlogo.webp';
import AuthLeftPanel from "../../../User/Auth/LeftAuthPanel/AuthLeftPanel";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from '../../../../authentication/AuthContext';
import { apiErrorMessage } from '../../../../utils/apiErrors';
import { saveCredential } from '../../../../utils/saveCredential';

// Signup is split over two steps so the first screen only asks for what every
// site asks for — email, name, password. The fields people hesitate over
// (company, phone, country) come after they've already committed, which is the
// whole point: nobody abandons on step 2 the way they abandon a wall of inputs.
//
// Step 2 is entirely skippable. Everything on it is optional at the database
// level, and the dashboard's setup card asks for it later — so someone who just
// wants in can be done after step 1.
//
// Submitting (or skipping) step 2 signs the user straight into the dashboard.
// Email verification still happens — a code goes out and the setup card asks
// for it — but it no longer stands between someone and the product, because a
// "check your inbox" wall is exactly where a signup funnel leaks.
//
// Both steps live inside ONE <form> and step 1 stays mounted (just hidden)
// while step 2 is on screen. That matters: browsers only offer to save a
// password when the form being submitted still contains the email and password
// fields. Unmounting step 1 would submit fine but silently cost the user their
// saved credential, so they'd be typing their password again on the next visit.
export default function Signup() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [screenloader, setScreenloader] = useState(false);
  const [step, setStep] = useState(1);
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [organization, setOrganization] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [fnameError, setFnameError] = useState("");
  const [lnameError, setLnameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [countryError, setCountryError] = useState("");
  const [organizationError, setOrganizationError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [resellerCode, setResellerCode] = useState("");
  const [resellerCodeError, setResellerCodeError] = useState("");
  const [resellerFromLink, setResellerFromLink] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const options = useMemo(() => countryList().getData(), []);
  const [searchParams] = useSearchParams();

  // Reseller code can be embedded in the signup link shared by a reseller,
  // e.g. https://app.cadomation.com/signup?reseller=AB3CD5EF
  useEffect(() => {
    const codeFromLink =
      searchParams.get("reseller") ||
      searchParams.get("reseller_code") ||
      searchParams.get("ref") ||
      "";
    const sanitized = sanitizeResellerCode(codeFromLink);
    if (sanitized) {
      setResellerCode(sanitized);
      setResellerFromLink(true);
    }
  }, [searchParams]);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth <= 600);
    checkScreen(); window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  // Firefox and Safari don't implement the Credential Management API and fall
  // back to the browser's own form heuristic — which the mounted-but-hidden
  // step 1 keeps alive by leaving email and password in the submitted form.
  const offerCredentialToBrowser = () =>
    saveCredential({ email, password, name: `${fname} ${lname}`.trim() });

  const dashboardFor = (userType) => {
    if (userType === "reseller") return "/Admin/ResellerDashboard";
    if (userType === "admin") return "/Admin/Dashboard";
    return "/User/Dashboard";
  };

  const Signup = async (skipDetails) => {
    await axios.post(`${process.env.REACT_APP_API_URL}/auth/signup`, {
      email: email,
      password: password,
      fname: fname,
      lname: lname,
      recaptchaToken: recaptchaToken,
      // Skipping omits the optional details entirely; the server stores NULL
      // for them and the dashboard prompts for them later.
      ...(skipDetails ? {} : {
        country: country.label,
        organization: organization,
        phone_number: phoneNumber,
      }),
      // The reseller code is attribution, not a profile detail, so it is sent
      // whether or not step 2 was filled in — a skipped signup that arrived on
      // a reseller's invite link must still be credited to them. Covers both
      // the ?reseller= parameter and a code typed by hand.
      ...(resellerCode.trim() !== "" && { reseller_code: resellerCode.trim().toUpperCase() }),
    })
      .then(async res => {
        setScreenloader(false);
        if (res.data.status) {
          if (res.data.status === 1) {
            // Offer the credential to the password manager BEFORE navigating —
            // Chrome drops a pending save if the tab moves on too quickly.
            await offerCredentialToBrowser();

            localStorage.setItem("accessToken", res.headers['access-token'] || res.data.data?.accessToken);
            localStorage.setItem("refreshToken", res.headers['refresh-token'] || res.data.data?.refreshToken);
            localStorage.setItem("userType", res.data.userType);
            localStorage.setItem("email", email);

            // Install synchronously so the route guard sees "ready" with the
            // right userType and permissions before we navigate — otherwise it
            // reads the pre-signup "anon" state and bounces to /signin.
            setSession({
              userType: res.data.userType,
              role: res.data.role,
              roleName: res.data.roleName,
              permissions: res.data.permissions,
              verified: res.data.verified,
              profileComplete: res.data.profileComplete,
              email,
            });

            toast.success("Welcome to Cadomation!");
            if (res.data.codeSent === false) {
              toast.error("We couldn't send your verification email. You can resend it from the banner on your dashboard.");
            }
            navigate(dashboardFor(res.data.userType), { replace: true });
            return;
          } else if (res.data.status === -2) {
            setResellerCodeError("Invalid reseller code. Please check and try again.");
            setResellerFromLink(false);
            resetCaptcha();
            return;
          } else if (res.data.status === -1) {
            // Email is taken — that's a step 1 field, so send them back to it
            // rather than showing the error on a screen that can't fix it.
            setEmailError("This email is already registered. Try signing in instead.");
            setStep(1);
            resetCaptcha();
            return;
          } else {
            toast.error(res.data.message);
            resetCaptcha();
            return;
          }
        }
      })
      .catch(error => {
        setScreenloader(false);
        console.log(error?.response?.data);
        toast.error(apiErrorMessage(error, "An unexpected error occurred."));
        resetCaptcha();
        return;
      });
  };

  const resetCaptcha = () => {
    if (captchaRef.current) {
      captchaRef.current.reset();
    }
    setRecaptchaToken(null);
  }

  // Step 1: the fields every signup form on the internet asks for.
  const validateStep1 = () => {
    setFnameError("");
    setLnameError("");
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");

    let isValid = true;

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      setEmailError("Invalid email address");
      isValid = false;
    } else if (email.length > 50) {
      setEmailError("Email should not exceed 50 characters");
      isValid = false;
    }
    if (fname === "" || fname.length < 2 || fname.length > 30) {
      setFnameError("First Name is required (2-30 characters)");
      isValid = false;
    }
    if (lname === "" || lname.length < 2 || lname.length > 30) {
      setLnameError("Last Name is required (2-30 characters)");
      isValid = false;
    }
    if (password === "" || password.length < 6 || password.length > 20) {
      setPasswordError("Password is required (6-20 characters)");
      isValid = false;
    } else if (!isStrongPassword(password)) {
      setPasswordError("Password should be strong (include alphabets and numbers)");
      isValid = false;
    }
    if (confirmPassword === "") {
      setConfirmPasswordError("Confirm Password is required");
      isValid = false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      isValid = false;
    }

    return isValid;
  };

  // Sent on both paths, so checked on both paths. The server requires 7-12
  // alphanumeric characters; catching that here turns a raw validation error
  // into a message pointing at the field that caused it.
  const validateResellerCode = () => {
    setResellerCodeError("");
    const code = resellerCode.trim();
    if (code === "") return true;
    if (code.length < 7 || code.length > 12) {
      setResellerCodeError("A reseller code is 7-12 characters. Clear this field if you don't have one.");
      // A code that arrived from an invite link renders read-only, which would
      // leave the user staring at an error they cannot act on. Hand the field
      // back to them, same as the server's "invalid code" response does.
      setResellerFromLink(false);
      return false;
    }
    return true;
  };

  // Step 2: everything the account itself needs, asked once they're invested.
  const validateStep2 = () => {
    setCountryError("");
    setOrganizationError("");
    setPhoneNumberError("");

    let isValid = true;

    if (country === "") {
      setCountryError("Country is required");
      isValid = false;
    }
    if (organization === "" || organization.length < 3 || organization.length > 50) {
      setOrganizationError("Organization is required (3-50 characters)");
      isValid = false;
    }
    if (!phoneNumber) {
      setPhoneNumberError("Phone number is required");
      isValid = false;
    } else if (phoneNumber.length < 6) {
      setPhoneNumberError("Please enter a valid phone number");
      isValid = false;
    }

    return isValid;
  };

  // Shared by "Create Account" and "Skip and finish later" — the only
  // difference is whether step 2's fields are validated and sent.
  const runSignup = async (skipDetails) => {
    // Re-check step 1: its inputs are still live in the DOM behind step 2, so a
    // password manager overwriting one after the user moved on would otherwise
    // slip past unvalidated.
    if (!validateStep1()) {
      setStep(1);
      return;
    }
    if (!skipDetails && !validateStep2()) {
      return;
    }
    if (!validateResellerCode()) {
      // The field lives on step 2, so make sure they can see what's wrong.
      setStep(2);
      return;
    }
    // The captcha guards account creation itself, so skipping the optional
    // details doesn't skip it.
    if (!captchaLoaded) {
      toast.error("Captcha is not loaded yet. Please wait.");
      return;
    }
    if (!recaptchaToken) {
      toast.error("Please verify you're not a robot.");
      return;
    }

    setScreenloader(true);
    await Signup(skipDetails);
  };

  // One submit handler for both steps, so pressing Enter on step 1 advances
  // instead of firing off a half-filled signup request.
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (step === 1) {
      if (validateStep1()) {
        setStep(2);
      }
      return;
    }
    await runSignup(false);
  };

  const goBackToStep1 = () => {
    // The captcha widget unmounts with step 2, so drop its token and loaded
    // flag — otherwise coming forward again would submit a stale token.
    resetCaptcha();
    setCaptchaLoaded(false);
    setStep(1);
  };

  const sanitizeResellerCode = (value) =>
    value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 12);

  const isStrongPassword = (password) => {
    const strongPasswordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d\W_]{6,}$/;
    return strongPasswordRegex.test(password);
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <>
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className="signin-page">
        <div className="auth-wrapper">
          <AuthLeftPanel />
          <div className="right-panel">
            <Card className="signin-card">
              <Card.Body>
                <div className="logo-wrapper text-center mb-4">
                  <img src={logo} alt="Cadomation" className="signin-logo" />
                </div>
                <h4 className="form-title text-center">Sign Up for Cadomation</h4>
                <p className="form-sub text-center">
                  {step === 1
                    ? "Create your account — it takes less than a minute."
                    : "Optional — this helps us set up licenses and quotes for you. You can skip it and add it later."}
                </p>
                <div className="container">
                  <div className="signup-steps">
                    <div className={`signup-step ${step === 1 ? "is-active" : "is-done"}`}>
                      <span className="signup-step-dot">1</span>
                      <span className="signup-step-label">Account</span>
                    </div>
                    <span className={`signup-step-line ${step === 2 ? "is-done" : ""}`} />
                    <div className={`signup-step ${step === 2 ? "is-active" : ""}`}>
                      <span className="signup-step-dot">2</span>
                      <span className="signup-step-label">Details</span>
                    </div>
                  </div>

                  <form onSubmit={handleFormSubmit} noValidate>
                    {/* Step 1 — kept in the DOM on step 2 so the browser still
                        sees email + password in the form it submits. */}
                    <div className="signup-step-panel" hidden={step !== 1}>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3" controlId="firstname">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="fname"
                              autoComplete="given-name"
                              placeholder="John"
                              className="form-input"
                              value={fname}
                              onChange={(e) => { setFname(e.target.value); setFnameError(""); }}
                              isInvalid={!!fnameError}
                            />
                            <Form.Control.Feedback type="invalid">
                              {fnameError}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3" controlId="lastname">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="lname"
                              autoComplete="family-name"
                              placeholder="Doe"
                              className="form-input"
                              value={lname}
                              onChange={(e) => { setLname(e.target.value); setLnameError(""); }}
                              isInvalid={!!lnameError}
                            />
                            <Form.Control.Feedback type="invalid">
                              {lnameError}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="row">
                        <div className="col-md-12">
                          <Form.Group className="mb-3" controlId="email">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              autoComplete="username"
                              placeholder="e.g username@gmail.com"
                              className="form-input"
                              value={email}
                              maxLength={50}
                              onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                              isInvalid={!!emailError}
                            />
                            <Form.Control.Feedback type="invalid">
                              {emailError}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </div>
                      </div>
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3" controlId="password">
                            <Form.Label>Create Password</Form.Label>
                            <div className="position-relative">
                              <Form.Control
                                type={showPassword ? "text" : "password"}
                                name="password"
                                autoComplete="new-password"
                                placeholder="Password"
                                className="form-input pe-5"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
                                isInvalid={!!passwordError}
                              />
                              <span
                                className="password-toggle"
                                onClick={toggleShowPassword}
                                style={{ right: passwordError ? "40px" : "14px" }}
                                role="button"
                                tabIndex={0}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                              >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                              </span>
                            </div>
                            <Form.Control.Feedback type="invalid">
                              {passwordError}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group className="mb-3" controlId="confirmpassword">
                            <Form.Label>Confirm Password</Form.Label>
                            <div className="position-relative">
                              <Form.Control
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                autoComplete="new-password"
                                placeholder="Confirm Password"
                                className="form-input pe-5"
                                value={confirmPassword}
                                onChange={(e) => { setConfirmPassword(e.target.value); setConfirmPasswordError(""); }}
                                isInvalid={!!confirmPasswordError}
                              />
                              <span
                                className="password-toggle"
                                onClick={toggleShowConfirmPassword}
                                style={{ right: confirmPasswordError ? "40px" : "14px" }}
                                role="button"
                                tabIndex={0}
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                              >
                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                              </span>
                            </div>
                            <Form.Control.Feedback type="invalid">
                              {confirmPasswordError}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="d-grid">
                        <Button type="submit" className="btn-signin">
                          Continue
                        </Button>
                        <p className="text-center mt-3 mb-0">
                          Already have an account?{" "}
                          <span onClick={() => { navigate(`/signin`, { replace: true }) }} className="sign-up-link" style={{ color: '#2D3748' }}>
                            Sign in
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Step 2 — the rest of the account details. */}
                    <div className="signup-step-panel" hidden={step !== 2}>
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3" controlId="organization">
                            <Form.Label>Organization</Form.Label>
                            <Form.Control
                              type="text"
                              name="organization"
                              autoComplete="organization"
                              placeholder="Organization"
                              className="form-input"
                              value={organization}
                              onChange={(e) => { setOrganization(e.target.value); setOrganizationError(""); }}
                              isInvalid={!!organizationError}
                            />
                            <Form.Control.Feedback type="invalid">
                              {organizationError}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group className="mb-3" controlId="country">
                            <Form.Label>Country</Form.Label>
                            <Select
                              classNamePrefix="react-select"
                              placeholder="Select Country"
                              options={options}
                              value={country}
                              name="country"
                              onChange={(e) => { setCountry(e); setCountryError(""); }}
                            />
                            {countryError && (
                              <div className="signup-field-error">{countryError}</div>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3" controlId="phoneNumber">
                            <Form.Label>Phone Number</Form.Label>
                            <div className="PhoneInput-wrapper">
                              <PhoneInput
                                international
                                placeholder="e.g. +1 234 567 890"
                                value={phoneNumber}
                                onChange={(value) => { setPhoneNumber(value); setPhoneNumberError(""); }}
                                id="phoneNumber"
                                name="phone_number"
                                autoComplete="tel"
                              />
                            </div>
                            {phoneNumberError && (
                              <div className="signup-field-error">{phoneNumberError}</div>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3" controlId="resellerCode">
                            <Form.Label>
                              Reseller Code <span className="text-muted" style={{ fontWeight: 'normal', fontSize: '13px' }}>(optional)</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="reseller_code"
                              autoComplete="off"
                              placeholder="e.g. AB3CD5EF"
                              className="form-input"
                              value={resellerCode}
                              maxLength={12}
                              readOnly={resellerFromLink}
                              onChange={(e) => { setResellerCode(sanitizeResellerCode(e.target.value)); setResellerCodeError(""); }}
                              isInvalid={!!resellerCodeError}
                              style={resellerFromLink ? { backgroundColor: '#f1f5f9' } : undefined}
                            />
                            <Form.Control.Feedback type="invalid">
                              {resellerCodeError}
                            </Form.Control.Feedback>
                            {resellerFromLink && !resellerCodeError && (
                              <div className="d-flex justify-content-between align-items-center mt-1">
                                <small style={{ color: '#016641', fontWeight: 600 }}>
                                  Reseller code applied from your invite link.
                                </small>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className="sign-up-link"
                                  style={{ fontSize: '13px', color: '#2D3748' }}
                                  onClick={() => { setResellerFromLink(false); setResellerCode(""); }}
                                >
                                  Change
                                </span>
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* Mounted with step 2 only, so the challenge is on screen
                          and interactive at the moment it's actually needed. */}
                      {step === 2 && (
                        <TurnstileCaptcha
                          ref={captchaRef}
                          onVerify={setRecaptchaToken}
                          onExpire={() => setRecaptchaToken(null)}
                          onLoad={() => setCaptchaLoaded(true)}
                        />
                      )}

                      <div className="signup-actions">
                        <Button
                          type="button"
                          className="btn-signup-back"
                          onClick={goBackToStep1}
                        >
                          Back
                        </Button>
                        <Button type="submit" className="btn-signin">
                          Create Account
                        </Button>
                      </div>
                      <p className="text-center mt-3 mb-0">
                        Rather not right now?{" "}
                        <span
                          role="button"
                          tabIndex={0}
                          className="sign-up-link"
                          onClick={() => runSignup(true)}
                        >
                          Skip and finish later
                        </span>
                      </p>
                    </div>
                  </form>
                </div>
              </Card.Body> {isMobile && <Footer />} </Card>
          </div>
        </div>
        {!isMobile && <Footer />}
      </div>
    </>
  );
}
